'use client'

import type { BigIntish, Fetchable } from '@morpho-org/blue-sdk'

import { type InterfaceToken, toBigInt } from '@morpho-org/dapp-core'
import { formatLongString, isDefined } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils'
import { Badge } from '@repo/ui'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

import { Avatar } from './Avatar'
import { HoverableCell } from './HoverableCell'

export interface CellTokenPriceProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'content'
> {
  /** Token object with decimals, price, symbol, and logo */
  token?: InterfaceToken | null
  /** Token amount */
  amount: Fetchable<BigIntish>
  /** USD value (18 decimals) */
  value?: Fetchable<bigint>
  /** Net APY (18 decimals) */
  netApy?: Fetchable<bigint>
  /** Content to display in hover card */
  content?: ReactNode
  /** Render prop for warnings */
  renderWarnings?: ReactNode
  /** Show token avatar */
  withAvatar?: boolean
  /** Show net APY badge */
  withNetApy?: boolean
  /** Show rewards icon in APY badge */
  renderRewardsIcon?: ReactNode
  /** Disable hover card interaction */
  isHoverCardDisabled?: boolean
  /** Maximum length for token symbol before truncation */
  maxLength?: number
  /** Additional Tailwind classes for the amount span (v5 TextVariant parity) */
  amountClassName?: string
}

/**
 * CellTokenPrice component - Tailwind replica of dapp-ui-v5 CellTokenPrice
 *
 * Full-featured token display with amount, value, APY, and optional rewards.
 * Supports hover card content for additional details.
 *
 * @example
 * ```tsx
 * <CellTokenPrice
 *   token={token}
 *   amount={1000000000000000000n}
 *   value={1500000000000000000000n}
 *   netApy={100000000000000000n}
 *   withAvatar
 *   withNetApy
 *   content={<SupplyHoverCard />}
 * />
 * ```
 */
const CellTokenPrice = forwardRef<HTMLDivElement, CellTokenPriceProps>(
  (
    {
      token,
      amount,
      value,
      netApy,
      content,
      renderWarnings,
      withAvatar,
      withNetApy,
      renderRewardsIcon,
      isHoverCardDisabled = false,
      maxLength = 16,
      amountClassName,
      className,
      ...props
    },
    ref
  ) => {
    const formattedSymbol = formatLongString(token?.symbol ?? '', maxLength)

    const getFormatAmount = () => {
      if (!isDefined(amount) || !isDefined(token) || !isDefined(token.decimals)) {
        return '-'
      }
      const amountBigInt = toBigInt(amount)

      return formatters.short
        .digits(getDigitsFromPriceAndAmount(token.price, amountBigInt, token.decimals))
        .default('-')
        .unit(formattedSymbol)
        .of(amountBigInt, token.decimals)
    }

    const formatAmount = getFormatAmount()
    const formatValue = formatters.short.unit('$').digits(2).of(value, 18)
    const formatNetApy = formatters.short.unit('%').digits(2).of(netApy, 18)

    return (
      <div ref={ref} className={cn('gap-2xs flex items-center', className)} {...props}>
        <HoverableCell hoverCardContent={content} isDisabled={isHoverCardDisabled}>
          <div className="gap-xs flex">
            <div className="gap-2xs flex items-center">
              {withAvatar && <Avatar size="small" src={token?.logoURI} />}

              <div className={cn('flex items-center', withNetApy ? 'gap-xs' : 'gap-[10px]')}>
                <span className={cn('text-xs text-foreground leading-normal', amountClassName)}>
                  {formatAmount}
                </span>
                {formatAmount && formatValue && (
                  <Badge variant="tertiary" size="sm">
                    {formatValue}
                  </Badge>
                )}

                {withNetApy && (
                  <Badge variant="tertiary" size="sm">
                    <span className="inline-flex items-center gap-[2px]">
                      {formatNetApy}
                      {renderRewardsIcon}
                    </span>
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </HoverableCell>
        {renderWarnings}
      </div>
    )
  }
)

CellTokenPrice.displayName = 'CellTokenPrice'

export { CellTokenPrice }
